/**
 * Rule Review Console types — mirrors app/api/routes/rule_reviews.py
 * response models.
 */

export type ReviewDecision =
  | 'agree'
  | 'agree_optional'
  | 'agree_carrier_overridable'
  | 'contest'
  | 'needs_discussion';

export type ReviewDiscipline = 'legal' | 'coverage' | 'trade' | 'estimating';

export interface CategoryProgress {
  category: string;
  total_rules: number;
  reviewed_count: number;
  pct_complete: number;
}

export interface RuleForReview {
  id: string;
  name: string;
  plain_english_render?: string | null;
  description: string;
  category: string;
  domains: string[];
  tags: string[];
  citations: Array<{
    // Companion-category rules carry citations without source_type
    source_type?: string | null;
    reference?: string | null;
    summary?: string | null;
    jurisdiction?: string | null;
  }>;
  status: string;
  severity: string;
  rule_type: string;
  my_decision?: ReviewDecision | null;
  my_comment?: string | null;
  my_contest_reason?: string | null;
  is_discipline_match: boolean;
}

export interface SubmitReviewRequest {
  decision: ReviewDecision;
  comment?: string;
  contest_reason?: string;
}

export interface SubmitReviewResponse {
  rule_id: string;
  decision: ReviewDecision;
  saved: boolean;
}

export interface ProgressSummary {
  total_rules: number;
  total_reviewed: number;
  pct_complete: number;
  categories: CategoryProgress[];
}

export interface ConsensusSummary {
  rule_id: string;
  rule_name: string;
  category: string;
  agree_count: number;
  agree_optional_count: number;
  agree_carrier_count: number;
  contest_count: number;
  needs_discussion_count: number;
  total_reviews: number;
  has_discrepancy: boolean;
  discussion_status?: string | null;
}

export interface LedgerEntry {
  reviewer_id: string;
  email?: string | null;
  total_reviews: number;
  agree_count: number;
  contest_count: number;
  disciplines: ReviewDiscipline[];
}

export interface InviteReviewerRequest {
  email: string;
  disciplines: ReviewDiscipline[];
}

export interface ResolveDiscrepancyRequest {
  resolution: string;
  new_status?: string;
  new_severity?: string;
}

export const DECISION_LABELS: Record<ReviewDecision, string> = {
  agree: 'Agree',
  agree_optional: 'Agree (optional)',
  agree_carrier_overridable: 'Agree (carrier-overridable)',
  contest: 'Contest',
  needs_discussion: 'Needs discussion',
};

export const DECISION_COLORS: Record<ReviewDecision, string> = {
  agree: 'bg-green-100 text-green-800 border-green-300',
  agree_optional: 'bg-blue-100 text-blue-800 border-blue-300',
  agree_carrier_overridable: 'bg-sky-100 text-sky-800 border-sky-300',
  contest: 'bg-red-100 text-red-800 border-red-300',
  needs_discussion: 'bg-amber-100 text-amber-800 border-amber-300',
};

export const DISCIPLINE_LABELS: Record<ReviewDiscipline, string> = {
  legal: 'Legal',
  coverage: 'Coverage',
  trade: 'Trade',
  estimating: 'Estimating',
};
